import React, { useEffect, useState } from 'react';
import { API_URL } from '../../config/env';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useGrievance } from '../../contexts/GrievanceContexts'; 
import { 
  FileText, 
  TrendingUp, 
  Clock,
  CheckCircle,
  ScrollIcon
} from 'lucide-react';



interface SiteSettings {
  description: string;      // Nepali
  description_en: string;    // English
}


const HomePage: React.FC = () => {
  const { language } = useLanguage(); 
  const { totalGrievance, resolvedGrievances, inProgressGrievances, pendingGrievances, averageResolutionDays} = useGrievance();
  // const features = [
  //   {
  //     icon: FileText,
  //     title: language === 'en' ? 'Submit Grievances' : 'गुनासो पेश गर्नुहोस्',
  //     description: language === 'en' 
  //       ? 'Easily submit your grievances online with detailed information'
  //       : 'आफ्ना गुनासोहरू अनलाइन सजिलै पेश गर्नुहोस्'
  //   },
  //   {
  //     icon: Search,
  //     title: language === 'en' ? 'Track Status' : 'स्थिति ट्र्याक गर्नुहोस्',
  //     description: language === 'en'
  //       ? 'Track the status of your grievances in real-time'
  //       : 'वास्तविक समयमा आफ्ना गुनासोहरूको स्थिति ट्र्याक गर्नुहोस्'
  //   },
  //   {
  //     icon: Shield,
  //     title: language === 'en' ? 'Secure & Private' : 'सुरक्षित र निजी',
  //     description: language === 'en'
  //       ? 'Your data is confidential and protected. '
  //       : 'तपाईंको डाटा गोप्य र सुरक्षित छ।'
  //   },
  //   {
  //     icon: Globe,
  //     title: language === 'en' ? 'Multilingual' : 'बहुभाषिक',
  //     description: language === 'en'
  //       ? 'Available in Nepali and English languages'
  //       : 'नेपाली र अंग्रेजी भाषाहरूमा उपलब्ध छ।'
  //   }
  // ];



  const stats = [
    {
      icon: FileText,
      value: totalGrievance,
      label: language === 'en' ? 'Total Grievances' : 'कुल गुनासो'
    },
    {
      icon: CheckCircle,
      value: resolvedGrievances,
      label: language === 'en' ? 'Resolved' : 'समाधान गरिएका'
    },
    {
      icon: TrendingUp,
      value: pendingGrievances,
      label: language === 'en' ? 'Pending' : 'समाधान गर्न बाँकि'
    },
    {
      icon: ScrollIcon,
      value: inProgressGrievances,
      label: language === 'en' ? 'In Progressed' : 'समाधान हुदै'
    },
    {
      icon: Clock,
      value: averageResolutionDays,
      label: language === 'en' ? 'Avg. Days' : 'औसत दिन'
    }
  ];
  
  
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/settings`);
        const data = await res.json();
        setSiteSettings(data);
      } catch (err) {
        console.error(err);
      }
    };
    loadSettings();
  }, []);


  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-primary-100">
        <div className="absolute inset-0 bg-white"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-3xl font-bold text-[#fa0101] mb-6">
              {language === 'en' ? 'Hello CM' : 'हेलो सिएम'}
            </h1>
            <p className="text-xl md:text-1xl text-primary-900 mb-8 max-w-3xl mx-auto">
              {siteSettings 
                ? (language === 'en' ? siteSettings.description_en : siteSettings.description)
                : 'Loading...'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/submit"
                className="btn-outline btn-lg text-lg px-8 py-2 bg-[#4584e1] text-white hover:bg-white hover:text-blue-600 border border-blue-600"
              >
                {language === 'en' ? 'Submit Grievance' : 'गुनासो पेश गर्नुहोस्'}
              </Link>

              <Link
                to="/track"
                className="btn-outline btn-lg text-lg px-8 py-2 bg-[#4584e1] text-white hover:bg-white hover:text-blue-600 border border-blue-600"
              >
                {language === 'en' ? 'Track Grievance' : 'गुनासो ट्रयाक गर्नुहोस्'}
              </Link>

            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <stat.icon className="h-6 w-6 text-primary-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      {/* <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Why Hello CM?' : 'Hello CM किन?'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'en'
                ? 'Our platform provides a comprehensive solution for grievance management with transparency and accountability'
                : 'हाम्रो प्लेटफर्मले पारदर्शिता र जवाफदेहिता प्रवर्दनका लागि गुनासो व्यवस्थापन गरी समाधान प्रदान गर्न।'
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <feature.icon className="h-8 w-8 text-primary-300" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div> */}

      {/* How It Works Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'How It Works' : 'यो कसरी काम गर्दछ'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'en'
                ? 'Simple steps to submit and track your grievances'
                : 'आफ्ना गुनासोहरू पेश गर्ने र ट्रयाक गर्ने सरल चरणहरू'
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">


            <Link
              to="/submit"
              className="group block h-full rounded-2xl border border-gray-200 bg-white p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#4584e1] hover:shadow-xl"
            >
              <div className="w-16 h-16 bg-[#4584e1] rounded-full flex items-center justify-center mx-auto mb-6 transition-transform duration-300 group-hover:scale-110">
                <span className="text-white text-2xl font-bold">1</span>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {language === 'en' ? 'Submit' : 'पेश गर्नुहोस्'}
              </h3>

              <p className="text-gray-600 leading-relaxed">
                {language === 'en'
                  ? 'Fill out the grievance form with all necessary details'
                  : 'सबै आवश्यक विवरणहरू सहित गुनासो फारम भर्नुहोस्।'}
              </p>

              {/* Button appears on hover */}
              <div className="mt-6 max-h-0 overflow-hidden opacity-0 transition-all duration-300 group-hover:max-h-20 group-hover:opacity-100">
                <span className="inline-flex items-center gap-2 rounded-lg bg-[#4584e1] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-[#3674d1]">
                  {language === 'en' ? 'Submit Now' : 'अहिले पेश गर्नुहोस्'}
                  <span className="transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </span>
              </div>
            </Link>


            <Link
              to="/track"
              className="group block h-full rounded-2xl border border-gray-200 bg-white p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#4584e1] hover:shadow-xl"
            >
              <div className="w-16 h-16 bg-[#4584e1] rounded-full flex items-center justify-center mx-auto mb-6 transition-transform duration-300 group-hover:scale-110">
                <span className="text-white text-2xl font-bold">2</span>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {language === 'en' ? 'Track' : 'ट्रयाक गर्नुहोस्'}
              </h3>

              <p className="text-gray-600 leading-relaxed">
                {language === 'en'
                  ? 'Use your unique grievance ID to track progress'
                  : 'गुनासोको प्रगति ट्र्याक गर्न आफ्नो गुनासो आईडी प्रयोग गर्नुहोस्'}
              </p>

              {/* Button appears on hover */}
              <div className="mt-6 max-h-0 overflow-hidden opacity-0 transition-all duration-300 group-hover:max-h-20 group-hover:opacity-100">
                <span className="inline-flex items-center gap-2 rounded-lg bg-[#4584e1] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-[#3674d1]">
                  {language === 'en' ? 'Track Now' : 'अहिले ट्र्याक गर्नुहोस्'}
                  <span className="transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </span>
              </div>
            </Link>

            <Link
              to="/track"
              className="group block h-full rounded-2xl border border-gray-200 bg-white p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#4584e1] hover:shadow-xl"
            >
              <div className="w-16 h-16 bg-[#4584e1] rounded-full flex items-center justify-center mx-auto mb-6 transition-transform duration-300 group-hover:scale-110">
                <span className="text-white text-2xl font-bold">3</span>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {language === 'en' ? 'Resolve Track' : 'समाधान प्राप्त गर्नुहोस्'}
              </h3>

              <p className="text-gray-600 leading-relaxed">
                {language === 'en'
                  ? 'Get updates and resolution for your grievance'
                  : 'आफ्नो गुनासोको सम्पूर्ण अपडेट र समाधान प्राप्त गर्नुहोस्।'}
              </p>

              {/* Button appears on hover */}
              <div className="mt-6 max-h-0 overflow-hidden opacity-0 transition-all duration-300 group-hover:max-h-20 group-hover:opacity-100">
                <span className="inline-flex items-center gap-2 rounded-lg bg-[#4584e1] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-[#3674d1]">
                  {language === 'en' ? 'View Resolution' : 'समाधान हेर्नुहोस्'}
                  <span className="transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-[#4584e1] py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {language === 'en' ? 'Ready to Submit Your Grievance?' : 'आफ्नो गुनासो पेश गर्न तयार हुनुहुन्छ?'}
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            {language === 'en'
              ? 'Join thousands of citizens who have already used our platform'
              : 'हाम्रो प्लेटफर्म प्रयोग गर्ने हजारौं नागरिकहरूसँग सामेल हुनुहोस्'
            }
          </p>
          <Link
            to="/submit"
            className="btn-lg bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
          >
            {language === 'en' ? 'Get Started Now' : 'अहिले सुरु गर्नुहोस्'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
